/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable indent */
import './styles.scss';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getGeneralInfoProjectForCouncil } from 'apis/projects.api';
import { ORGANIZATION_TYPES, PROJECT_ROLES } from 'constant';
import {
  formatDate,
  getAcadamicLevelShortHand,
  formatCurrency,
  formatFileName,
  formatFileNameProduct,
} from 'utils/func';
import DownloadScientificProfile from 'components/DownloadScientificProfile';
import WarningScientificProfileUrl from 'components/WarningScientificProfileUrl';
import { downloadFile } from 'apis/projectProposals.api';
import DownloadOrViewPdf from 'components/DownloadOrViewPdf';
import { getAllVersion } from 'apis/extendImplemetationTime.api';
import ResearchFields from './ResearchFields';
import PaticipantFields from './PaticipantFields';

// import { getAcadamicLevelShortHand } from 'utils/func';
// import { PROJECT_ROLES } from 'constant';

export default function GeneralInfo({
  summaryReportAllocatedExpenditures,
  projectCode,
  handleDownloadFile,
}) {
  const { t } = useTranslation();
  const { projectId } = useParams();

  // #region get general info
  const { data: generalInfoData, isLoading } = useQuery(['generalInfo', projectId], () =>
    getGeneralInfoProjectForCouncil(projectId)
  );
  const generalInfo = useMemo(() => generalInfoData?.data, [generalInfoData]);
  const projectProposalInfo = useMemo(() => generalInfo?.projectProposal, [generalInfo]);
  // const projectExpenditurePeriods = useMemo(() => generalInfo?.projectExpenditurePeriods, [generalInfo]);

    const { data: _projectDeadlineExtension } = useQuery(
    ['projectDeadlineExtension', projectId],
    () => getAllVersion(projectId)
  );

  const projectDeadlineExtension = useMemo(() => {
    if (!_projectDeadlineExtension?.data) {
      return [];
    }

    return (
      _projectDeadlineExtension.data?.filter(
        (item) => item.status === 'approved'
      ) || []
    );
  }, [_projectDeadlineExtension]);

  const [summaryReportPeriods, summaryReportFromOtherFund] = useMemo(
    () => [
      summaryReportAllocatedExpenditures?.filter((item) => !item?.fromOtherFund),
      summaryReportAllocatedExpenditures?.find((item) => item?.fromOtherFund),
    ],
    [summaryReportAllocatedExpenditures]
  );

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);

  const [owner, leadOrganizations] = useMemo(
    () => [
      projectProposalInfo?.projectProposalContacts.find(
        (contact) => contact?.projectRole?.code === PROJECT_ROLES.OWNER
      ),
      projectProposalInfo?.projectProposalOrganizations?.find(
        ({ type }) => type === ORGANIZATION_TYPES.LEAD
      ),
    ],
    [projectProposalInfo]
  );

  const participants = useMemo(
    () => [
      ...(projectProposalInfo?.projectProposalContacts ?? []).map((item) => ({
        ...item,
        isOrganizationPartner: false,
      })),
    ],
    [projectProposalInfo]
  );

  const partners = useMemo(
    () => [
      ...(projectProposalInfo?.projectProposalOrganizations ?? []).filter(
        ({ type }) => type === ORGANIZATION_TYPES.PARTNER
      ),
    ],
    [projectProposalInfo]
  );

  const handleDownloadPPFile = useCallback(
    (path) => {
      mutateDownloadFile(
        {
          id: projectProposalInfo?.id,
          path,
        },
        {
          onSuccess: async (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);

              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = formatFileName(path);

              link.click();
            }
          },
        }
      );
    },
    [mutateDownloadFile, projectProposalInfo?.id]
  );

  // eslint-disable-next-line react/prop-types
  const formatIndex = (value, props) => props.rowIndex + 1;

  const formatFullname = (value) => {
    if (value.isOrganizationPartner) {
      return value.chief;
    }

    return (
      <>
        {getAcadamicLevelShortHand(value.academicRank, value.academicDegree, t, true)}
        {value.fullname}
        {value?.contact?.user?.scientificProfileUrl ? (
          <DownloadScientificProfile
            contactId={value?.id || value?.contact?.id || value?.contactId}
            contactName={value?.fullname}
            academicRank={value?.academicRank}
            academicDegree={value?.academicDegree}
            handleDownloadFile={() => true}
          />
        ) : (
          <WarningScientificProfileUrl />
        )}
      </>
    );
  };

  const formatResearcherOrganization = (value) => {
    if (value.isOrganizationPartner) {
      return t('lang') === 'vi' ? value.name : value.englishName;
    }

    if (value.organization?.englishName === 'Other') return value.specificOrganizationName;
    return t('lang') === 'vi' ? value.organization?.name : value.organization?.englishName;
  };

  return (
    <div className="general-infor-r05">
      {/* A */}
      <h4 className="font-bold">{t('r05.a.title')}</h4>
      {/* A1 */}
      <section>
        <h5 className="my-2 ">{t('r05.a.a1.title')}</h5>
        <p className="my-2 pl-4">
          - <span className="font-semibold">{t('r05.a.a1.vietnameseName')}: </span>
          {projectProposalInfo?.vietnameseName}
        </p>
        <p className="my-2 pl-4">
          - <span className="font-semibold ">{t('r05.a.a1.englishName')}: </span>
          {projectProposalInfo?.englishName}
        </p>
      </section>
      {/* A2 */}
      <section>
        <h5>{t('r05.a.a2.title')}</h5>
        {projectProposalInfo?.projectProposalResearchFields && (
          <ResearchFields
            researchFields={projectProposalInfo?.projectProposalResearchFields ?? []}
          />
        )}
      </section>
      {/* A3  */}
      <section>
        <h5>{t('r05.a.a3.title')}</h5>
        <p className="flex-1 ml-5">
          <span className="font-semibold">
            {t('projectProposal.general.researchType.researchType')}:{' '}
          </span>
          {projectProposalInfo?.researchType?.code
            ? t(`researchTypes.${projectProposalInfo?.researchType?.code}`)
            : projectProposalInfo?.researchType?.name}
        </p>
      </section>
      {/* A4  */}
      <section>
        <h4>{t('r05.a.a4.title')}</h4>
        <p className="my-2">
          &emsp;- &ensp;{t('r05.a.a4.timeFrom')} {new Date(generalInfo?.startDate).getMonth() + 1}/
          {new Date(generalInfo?.startDate).getFullYear()} {t('r05.a.a4.timeTo')}{' '}
          {new Date(generalInfo?.endDate).getMonth() + 1}/
          {new Date(generalInfo?.endDate).getFullYear()}
        </p>
         {projectDeadlineExtension?.length > 0 ? (
          <>
            {projectDeadlineExtension.map((item, index) => (
              <p className="my-2" key={item.id}>
                &emsp;&emsp;- &ensp;{t('r05.a.a4.renewFromNumber', { index: index + 1 })}
                {'  '}
                {new Date(generalInfo?.endDate).getMonth() + 1}/{new Date(generalInfo?.endDate).getFullYear()}{' '}
                {t('r05.a.a4.timeTo')} {new Date(item?.extendDate).getMonth() + 1}/
                {new Date(item?.extendDate).getFullYear()}
              </p>
            ))}
          </>
        ) : (
          <p className="my-2">
            &emsp;- &ensp;{t('r05.a.a4.renewFrom')} ............ {t('r05.a.a4.renewTo')} ...........
          </p>
        )}
      </section>
      {/* A5  */}
      <section>
        <h4>{t('r05.a.a5.title')}</h4>
        <p className="my-2">
          &emsp;- &ensp;{t('r05.a.a5.totalExpenditure')}{' '}
          <span className="font-italic font-bold">
            {formatCurrency(generalInfo?.totalExpenditure)}{' '}
          </span>
          {t('r05.a.a5.include')}
        </p>
        {summaryReportPeriods?.length > 0 ? (
          <>
            {summaryReportPeriods.map((item, index) => (
              <p className="my-2" key={item?.id || index}>
                &emsp;&emsp;&emsp; + {t('r05.a.a5.expenditurePeriod')} {index + 1}
                {': '}
                <span className="font-italic font-bold">{formatCurrency(item?.amount)} </span>
                {t('r05.a.a5.followUp')} <span className="font-bold">{item?.decisionNo}</span>{' '}
                {t('r05.a.a5.day')}{' '}
                <span className="font-bold">{formatDate(item?.issueDate, 'dd/MM/yyyy')}</span>
                {item?.filename && (
                  <DownloadOrViewPdf
                    opts={{ path: item?.filename, folder: 'summary-report-allocated-expenditures' }}
                    fileName={formatFileNameProduct(
                      item?.filename || '',
                      projectCode,
                      'KP',
                      index + 1
                    )}
                    handleDownloadOrView={handleDownloadFile}
                    showIcon={false}
                  />
                )}
              </p>
            ))}
          </>
        ) : (
          <p className="my-2 font-italic">&emsp;&emsp;&emsp; {t('r05.a.a5.noExpenditurePeriod')}</p>
        )}

        <p className="my-2">
          &emsp;&emsp;- &ensp;{t('r05.a.a5.expenditureFromFunding')}{' '}
          <span className="font-italic font-bold">
            {formatCurrency(summaryReportFromOtherFund?.amount)}
          </span>
          {summaryReportFromOtherFund?.filename && (
            <DownloadOrViewPdf
              opts={{
                path: summaryReportFromOtherFund?.filename,
                folder: 'summary-report-allocated-expenditures',
              }}
              fileName={formatFileNameProduct(
                summaryReportFromOtherFund?.filename || '',
                projectCode,
                'KP',
                -1
              )}
              handleDownloadOrView={handleDownloadFile}
              showIcon={false}
            />
          )}
        </p>
      </section>
      {/* A6  */}
      <section>
        <h5 className="">{t('r05.a.a6.title')}</h5>
        <p className="my-2">
          <span className="font-semibold ml-3">{t('r05.a.a6.name')}</span>
          {`${getAcadamicLevelShortHand(owner?.academicRank, owner?.academicDegree, t, true)} ${
            owner?.fullname
          } ` ?? '................'}
        </p>
        <p className="my-2">
          <span className="font-semibold ml-3">{t('r05.a.a6.birthDate')}</span>
          {formatDate(owner?.dob) ?? '................'}
          <span className="font-semibold ml-6">{t('r05.a.a6.sex')}</span>
          {owner?.gender ? t(`gender.${owner?.gender}`) : '................'}
        </p>
        <p className="my-2">
          <span className="font-semibold ml-3">{t('r05.a.a6.company')}</span>
          {owner?.organization?.name ?? '................'}
        </p>
        <p className="my-2">
          <span className="font-semibold ml-3">{t('r05.a.a6.phone')}</span>
          {owner?.mobile ?? '................'}
          <span className="font-semibold ml-6">{t('r05.a.a6.email')}</span>
          {owner?.email ?? '..........'}
        </p>
      </section>
      {/* A7  */}
      <section>
        <h5 className="">{t('r05.a.a7.title')}</h5>
        <div>
          <p className="my-2">
            <span className="font-semibold ml-3">{t('r05.a.a7.companyName')}</span>
            {/* eslint-disable-next-line no-nested-ternary */}
            {leadOrganizations?.englishName === 'Other'
              ? leadOrganizations?.name
              : t('lang') === 'vi'
              ? leadOrganizations?.name
              : leadOrganizations?.englishName}
          </p>
          <p className="my-2">
            <span className="font-semibold ml-3">{t('r05.a.a7.leaderName')}</span>
            {leadOrganizations?.chief ?? '........'}
          </p>
          <div className="flex align-items-center my-2">
            <p className=" mb-0">
              <span className="font-semibold ml-3">{t('r05.a.a7.phone')}</span>
              {leadOrganizations?.phone ?? '........'}
            </p>
            <p className="mb-0">
              <span className="font-semibold ml-6">{t('r05.a.a7.fax')}</span>
              {leadOrganizations?.fax ?? '.......'}
            </p>
            <p />
          </div>
          <p className="my-2">
            <span className="font-semibold ml-3">{t('r05.a.a7.email')}</span>
            {leadOrganizations?.email ?? '........'}
          </p>
        </div>
      </section>
      {/* A8  */}
      <section>
        <h4>{t('r05.a.a9.title')}</h4>
        {partners?.length > 0 ? (
          partners.map((org, index) => (
            <div className="flex ml-5 mb-2 flex-column" key={org?.id || index}>
              <div className="mb-2">
                <span className="font-bold">
                  {t('projectProposal.general.organization.name')} {index + 1}:{' '}
                </span>
                <span>{org?.name}</span>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.partners.confirmationDocument')}:{' '}
                </span>
                {org?.partnerConfirmationUrl && (
                  <span
                    className="p-link text-primary underline"
                    onClick={() => handleDownloadPPFile(org?.partnerConfirmationUrl)}
                  >
                    {t('projectProposal.general.partners.attachment')}
                  </span>
                )}
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.chief')}:{' '}
                </span>
                <span>{org?.chief}</span>
              </div>
              <div className="flex flex-wrap">
                <div className="w-30rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.phone')}:{' '}
                  </span>
                  <span>{org?.phone}</span>
                </div>
                <div className="w-20rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.fax')}:{' '}
                  </span>
                  <span>{org?.fax}</span>
                </div>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.address')}:{' '}
                </span>
                <span>{org?.address}</span>
              </div>
              <div
                className={
                  index === projectProposalInfo?.projectProposalOrganizations?.length - 2
                    ? ''
                    : 'mb-3'
                }
              >
                <div>
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.partnerContribution')}:{' '}
                  </span>
                </div>
                <p>{org?.partnerContribution}</p>
              </div>
            </div>
          ))
        ) : (
          <span>({t('projectProposal.general.partners.noPartner')})</span>
        )}
      </section>

      <section>
        <h5 className="mt-2 mb-3">{t('r05.a.a8.title')}</h5>
        {projectProposalInfo?.projectProposalOrganizations && (
          <PaticipantFields
            data={participants}
            isLoading={isLoading}
            formatIndex={formatIndex}
            formatFullname={formatFullname}
            formatResearcherOrganization={formatResearcherOrganization}
          />
        )}
      </section>
    </div>
  );
}

GeneralInfo.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  summaryReportAllocatedExpenditures: PropTypes.array,
  projectCode: PropTypes.string,
  handleDownloadFile: PropTypes.func,
};

GeneralInfo.defaultProps = {
  projectCode: '',
  summaryReportAllocatedExpenditures: [],
  handleDownloadFile: () => {},
};
